<template>
  <lila-default-template>
    <template #mainContent>
      <div
        class="container units-page"
        :class="loadingContent ? 'loading large' : ''"
      >
        <h1 class="title">
          Unidades / Equipes
        </h1>

        <lila-message :alert-data="alert" />
        <div
          v-show="!loadingContent"
          class="units-filters"
        >
          <div class="units-filter units-filter--search">
            <label class="units-filter-label">
              Nome da Unidade / Equipe
            </label>
            <form @submit.prevent="applySearch">
              <lila-input
                v-model="filters.search"
                type="inputSearch"
                class="with-shadow"
                placeholder="Pesquisar"
                :btn-clear-search="filters.searchActive"
                @custom-clear-search-input-event="clearSearch"
              />
            </form>
          </div>

          <template v-if="canManage">
            <span class="units-divider" />

            <div class="units-filter units-filter--add">
              <label class="units-filter-label">Adicionar Unidade / Equipe</label>
              <button
                type="button"
                class="units-add-btn"
                @click="addUnit"
              >
                Adicionar
              </button>
            </div>
          </template>
        </div>

        <div
          v-show="!loadingContent"
          class="uh-list"
        >
          <div class="uh-header">
            <span>Nome da Unidade / Equipe</span>
            <span>Tipo</span>
            <span>Ações</span>
          </div>

          <p
            v-if="isSearching && pagedRoots.length === 0"
            class="units-empty has-text-centered"
          >
            Nenhum resultado para "{{ filters.search }}".
          </p>
          <p
            v-else-if="!isSearching && pagedRoots.length === 0"
            class="units-empty has-text-centered"
          >
            Nenhuma unidade encontrada.
          </p>

          <div
            v-else
            role="treegrid"
            aria-label="Unidades e equipes"
          >
            <div
              v-for="root in pagedRoots"
              :key="root.id"
              class="uh-card"
            >
              <units-hierarchy-row
                :node="root"
                :level="0"
                :is-expanded="isExpanded"
                :can-manage="canManage"
                @toggle="toggleExpanded"
                @edit="editRow"
                @delete="openDeleteModal"
              />
            </div>
          </div>
        </div>

        <lila-pagination
          v-show="!loadingContent && rootCount > 0"
          :pagination-config="pagination"
          @custom-change-page-event="changePage"
        />
      </div>

      <teleport to="body">
        <div
          v-if="deleteModal.show"
          class="units-modal-root"
        >
          <div
            class="units-modal-bg"
            @click="closeDeleteModal"
          />
          <div class="units-modal-card">
            <header
              class="units-modal-head"
              :class="deleteModal.blocked
                ? 'units-modal-head--blocked'
                : 'units-modal-head--delete'"
            >
              <div class="units-modal-head-icon">
                <lila-svg-icon
                  :src="deleteModal.blocked
                    ? require('@assets/icons/info.svg')
                    : require('@assets/icons/delete.svg')"
                />
              </div>
              <div class="units-modal-head-texts">
                <p class="units-modal-title">
                  {{ deleteModal.blocked
                    ? "Atenção: vínculos encontrados"
                    : "Confirmar exclusão" }}
                </p>
                <p class="units-modal-subtitle">
                  {{ deleteModal.blocked
                    ? "Não é possível excluir enquanto houver vínculos."
                    : "Esta ação não pode ser desfeita" }}
                </p>
              </div>
            </header>

            <div class="units-modal-body">
              <template v-if="deleteModal.blocked">
                <p
                  v-if="deleteModal.childrenMessage"
                  class="units-modal-text"
                >
                  {{ deleteModal.childrenMessage }}
                </p>
                <p
                  v-if="deleteModal.childrenMessage"
                  class="units-modal-text"
                >
                  Mova ou remova as equipes e subunidades vinculadas antes de excluir.
                </p>
                <template v-else>
                  <p class="units-modal-text">
                    Para excluir este item, transfira os pacientes e/ou
                    profissionais vinculados para outra unidade.
                  </p>
                  <p class="units-modal-text">
                    Essa ação garante a continuidade dos serviços e o atendimento
                    aos pacientes.
                  </p>
                </template>
              </template>
              <template v-else>
                <p class="units-modal-text">
                  Você está prestes a excluir este item permanentemente.
                </p>
                <p class="units-modal-text">
                  Deseja realmente excluir
                  {{ deleteModal.row && deleteModal.row.isTeam ? "a equipe" : "a unidade" }}
                  <strong>{{ deleteModal.row ? deleteModal.row.name : "" }}</strong>?
                </p>
              </template>
            </div>

            <footer class="units-modal-foot">
              <button
                v-if="!deleteModal.blocked"
                type="button"
                class="units-modal-btn units-modal-btn--ghost"
                :disabled="deleteModal.deleting"
                @click="closeDeleteModal"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="units-modal-btn"
                :class="deleteModal.blocked
                  ? 'units-modal-btn--primary'
                  : 'units-modal-btn--danger'"
                :disabled="deleteModal.deleting"
                @click="confirmDelete"
              >
                {{ deleteModal.blocked
                  ? "Entendi"
                  : (deleteModal.deleting ? "Excluindo…" : "Excluir permanentemente") }}
              </button>
            </footer>
          </div>
        </div>
      </teleport>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import Input from "@components/shared/fields/Input.vue";
import SvgIcon from "@components/shared/images/SvgIcon.vue";
import Message from "@components/shared/message/Message.vue";
import Pagination from "@components/shared/pagination/Pagination.vue";
import UnitsHierarchyRow from "@components/shared/lila/UnitsHierarchyRow.vue";
import decodeToken from "@/js/decodeToken";
import useTeamService from "@/composables/useTeamService";
import {
  buildTeamHierarchy,
  sortRoots,
  countDescendants,
  filterHierarchy
} from "@/js/teamHierarchy";

const EXPANDED_STORAGE_KEY = "lila.unitsTreeExpanded";

export default {
  components: {
    "lila-default-template": DefaultTemplate,
    "lila-input": Input,
    "lila-svg-icon": SvgIcon,
    "lila-message": Message,
    "lila-pagination": Pagination,
    "units-hierarchy-row": UnitsHierarchyRow
  },
  setup() {
    // Serviço centralizado dos endpoints de Team (sem HTTP direto no componente).
    return { team: useTeamService() };
  },
  data() {
    return {
      loadingContent: false,
      alert: {
        show: false,
        type: "success",
        message: ""
      },
      roots: [],
      expandedState: this.loadExpandedState(),
      filters: {
        search: "",
        searchActive: false
      },
      pagination: {
        current: 1,
        totalPages: 0,
        perPage: 10
      },
      deleteModal: {
        show: false,
        blocked: false,
        deleting: false,
        childrenMessage: "",
        row: null
      }
    };
  },
  computed: {
    isSearching() {
      return this.filters.searchActive && this.filters.search.trim() !== "";
    },
    filteredRoots() {
      if (!this.isSearching) return this.roots;
      return filterHierarchy(this.roots, this.filters.search);
    },
    rootCount() {
      return this.filteredRoots.length;
    },
    pagedRoots() {
      const start = (this.pagination.current - 1) * this.pagination.perPage;
      return this.filteredRoots.slice(start, start + this.pagination.perPage);
    },
    // Gestão de unidades liberada para role >= 1 (decisão do time / documento).
    // O backend ainda exige InstitutionAdmin (role 2) no TeamController —
    // enquanto não relaxar, UnitAdmin (role 1) verá as ações mas tomará 403.
    canManage() {
      return this.professionalRole() >= 1;
    }
  },
  watch: {
    rootCount() {
      this.recalculatePagination();
    }
  },
  created() {
    if (this.$route.query.customMessageType) {
      this.$Utilities.showResultMessage(
        {
          customMessage: {
            type: this.$route.query.customMessageType,
            message: this.$route.query.customMessage
          }
        },
        this.alert
      );
    }
    this.getUnits();
  },
  methods: {
    professionalRole() {
      const session = this.$Session.get(this.$userSessionName);
      const decoded = session ? decodeToken(session.token.Value) : null;
      return decoded ? Number(decoded.ProfessionalRole ?? 0) : 0;
    },

    async getUnits() {
      this.loadingContent = true;
      try {
        const res = await this.team.list();
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.roots = sortRoots(buildTeamHierarchy(
          this.$Utilities.teamsWithPath(list, { includeUnits: true })
        ));
      } catch (error) {
        if (error.response) this.$Utilities.verifyToken(error.response.status);
        this.$Utilities.controlAlert(
          error.response ? error.response.data : null,
          this.alert,
          "danger",
          "Ocorreu um erro ao carregar as unidades."
        );
      } finally {
        this.loadingContent = false;
        this.recalculatePagination();
      }
    },

    editRow(node) {
      this.$router.push({ name: "editUnit", params: { id: node.id } });
    },

    async openDeleteModal(node) {
      this.deleteModal.row = node;
      this.deleteModal.blocked = false;
      this.deleteModal.deleting = false;
      this.deleteModal.childrenMessage = "";

      if (!node.isTeam) {
        const { equipes, subunidades } = countDescendants(node);
        if (equipes || subunidades) {
          const parts = [];
          if (equipes) parts.push(`${equipes} ${equipes === 1 ? "equipe" : "equipes"}`);
          if (subunidades) {
            parts.push(`${subunidades} ${subunidades === 1 ? "subunidade" : "subunidades"}`);
          }
          this.deleteModal.blocked = true;
          this.deleteModal.childrenMessage =
            `Esta unidade possui ${parts.join(" e ")} vinculadas.`;
          this.deleteModal.show = true;
          return;
        }
      }

      try {
        const [hasProfessionals, hasPatients] = await Promise.all([
          this.unitHasProfessionals(node.id),
          this.unitHasPatients(node.id)
        ]);
        this.deleteModal.blocked = hasProfessionals || hasPatients;
      } catch (error) {
        this.deleteModal.blocked = false;
      } finally {
        this.deleteModal.show = true;
      }
    },

    // Verifica vínculos pelos endpoints dedicados (sem carregar listas inteiras).
    async unitHasProfessionals(unitId) {
      const res = await this.team.hasProfessionals(unitId);
      return Boolean(
        this.$Utilities.getValueIgnoreCase(res.data, "HasProfessionals")
      );
    },

    async unitHasPatients(unitId) {
      const res = await this.team.hasPatients(unitId);
      return Boolean(
        this.$Utilities.getValueIgnoreCase(res.data, "HasPatients")
      );
    },

    closeDeleteModal() {
      if (this.deleteModal.deleting) return;
      this.deleteModal.show = false;
      this.deleteModal.row = null;
      this.deleteModal.blocked = false;
      this.deleteModal.childrenMessage = "";
    },

    async confirmDelete() {
      if (this.deleteModal.blocked) {
        this.closeDeleteModal();
        return;
      }

      const row = this.deleteModal.row;
      if (!row) {
        this.deleteModal.show = false;
        return;
      }

      this.deleteModal.deleting = true;
      try {
        await this.team.remove(row.id);
        this.deleteModal.show = false;
        this.deleteModal.row = null;
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          "Item excluído com sucesso."
        );
        await this.getUnits();
      } catch (error) {
        const status = error.response ? error.response.status : null;
        if (status) this.$Utilities.verifyToken(status);

        const rawMsg = this.deleteErrorMessage(error).toLowerCase();
        const isLinkError =
          status === 400 &&
          (rawMsg.includes("paciente") ||
            rawMsg.includes("profissional") ||
            rawMsg.includes("vínculo") ||
            rawMsg.includes("vinculo") ||
            rawMsg.includes("associad"));

        if (isLinkError) {
          this.deleteModal.blocked = true;
          this.deleteModal.childrenMessage = "";
        } else {
          this.deleteModal.show = false;
          this.$Utilities.controlAlert(
            error.response ? error.response.data : null,
            this.alert,
            "danger",
            "Não foi possível excluir o item."
          );
        }
      } finally {
        this.deleteModal.deleting = false;
      }
    },

    deleteErrorMessage(error) {
      const data = error.response ? error.response.data : null;
      if (!data) return "";
      if (typeof data === "string") return data;
      return (
        this.$Utilities.getValueIgnoreCase(data, "Message") ||
        this.$Utilities.getValueIgnoreCase(data, "message") ||
        ""
      );
    },

    addUnit() {
      this.$router.push({ name: "addUnit" });
    },

    applySearch() {
      this.filters.searchActive = this.filters.search.trim() !== "";
      this.pagination.current = 1;
    },

    clearSearch() {
      this.filters.search = "";
      this.filters.searchActive = false;
      this.pagination.current = 1;
    },

    changePage(page) {
      this.pagination.current = page;
    },

    recalculatePagination() {
      const total = Math.ceil(this.rootCount / this.pagination.perPage);
      this.pagination.totalPages = total;
      if (this.pagination.current > total) {
        this.pagination.current = total || 1;
      }
      if (this.pagination.current < 1) {
        this.pagination.current = 1;
      }
    },

    loadExpandedState() {
      try {
        const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch (error) {
        return {};
      }
    },

    saveExpandedState() {
      try {
        localStorage.setItem(
          EXPANDED_STORAGE_KEY,
          JSON.stringify(this.expandedState)
        );
      } catch (error) {
        // localStorage indisponível — segue só em memória.
      }
    },

    isExpanded(id) {
      if (this.isSearching) return true;
      const stored = this.expandedState[String(id)];
      return stored !== false;
    },

    toggleExpanded(id) {
      const key = String(id);
      const current = this.isExpanded(id);
      this.expandedState = { ...this.expandedState, [key]: !current };
      this.saveExpandedState();
    }
  }
};
</script>

<style lang="scss">
$lila-primary: #4f3f82;
$lila-accent: #9a397d;

.units-page {
  padding-top: 10px;
  font-family: "Montserrat", sans-serif;
  background: #f5f5f5;
}

.units-page .title {
  color: $lila-primary;
  font-weight: 700;
  margin-bottom: 24px;
}

.units-page .units-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 50px;
}

.units-page .units-filter {
  flex: 1;
  min-width: 200px;
}

.units-page .units-filter--search {
  flex: 0 1 420px;
}

.units-page .units-filter--add {
  flex: 0 0 auto;
  min-width: 0;
}

.units-page .units-filter-label {
  display: block;
  font-size: 13px;
  color: #6b6b6b;
  margin-bottom: 8px;
}

.units-page .units-divider {
  align-self: stretch;
  width: 1px;
  background: #e4e1ee;
  margin: 0 4px 0 auto;
}

.units-page .units-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: $lila-accent;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.units-page .units-add-btn:hover {
  background: darken($lila-accent, 6%);
}

.units-page .units-add-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.units-page .units-add-btn:disabled:hover {
  background: $lila-accent;
}

.units-page .uh-list {
  margin-bottom: 12px;
}

.units-page .uh-header {
  display: grid;
  grid-template-columns: 1fr 130px 90px;
  padding: 0 20px;
  margin-bottom: 15px;
}

.units-page .uh-header span {
  font-size: 14px;
  font-weight: 600;
  color: #2a2030;
}

.units-page .uh-card {
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 12px;
}

.units-page .units-empty {
  padding: 30px 0;
  color: #9a9a9a;
}

.units-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.units-modal-bg {
  position: absolute;
  inset: 0;
  background: rgba(36, 31, 51, 0.45);
}

.units-modal-card {
  position: relative;
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(36, 31, 51, 0.25);
  overflow: hidden;
}

.units-modal-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 26px;
}

.units-modal-head--delete {
  background: rgba(224, 62, 62, 0.08);
}

.units-modal-head--blocked {
  background: rgba(230, 162, 60, 0.12);
}

.units-modal-head-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #ffffff;
  flex: none;
}

.units-modal-head-icon svg,
.units-modal-head-icon img {
  width: 22px;
  height: 22px;
}

.units-modal-head--delete .units-modal-head-icon {
  color: #e03e3e;
}

.units-modal-head--blocked .units-modal-head-icon {
  color: #d68910;
}

.units-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: #2c2c2c;
}

.units-modal-subtitle {
  font-size: 13px;
  color: #6b6b6b;
  margin-top: 2px;
}

.units-modal-body {
  padding: 22px 26px;
}

.units-modal-text {
  font-size: 15px;
  color: #4a4a4a;
  line-height: 1.5;
}

.units-modal-text + .units-modal-text {
  margin-top: 12px;
}

.units-modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 26px 22px;
}

.units-modal-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.units-modal-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.units-modal-btn--ghost {
  background: transparent;
  color: #6b6680;
}

.units-modal-btn--danger {
  background: #e03e3e;
  color: #ffffff;
}

.units-modal-btn--primary {
  background: $lila-accent;
  color: #ffffff;
}
</style>
